import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recurringService, transactionService } from '../services/api-secure';
import { Spinner } from './Spinner';
import { calmTokens as t } from '../shared/tokens';
import { MONTHS_FR } from '../shared/locale';
import { isTemplateValidForMonth } from '../shared/recurrence';
import ForecastHero from './forecast/ForecastHero';
import ForecastStatsRow from './forecast/ForecastStatsRow';
import ForecastEmptyState from './forecast/ForecastEmptyState';
import ForecastCalendarHeader from './forecast/ForecastCalendarHeader';
import ForecastSageDayGroup from './forecast/ForecastSageDayGroup';
import { computeForecast, groupByDay } from './forecast/helpers';

export default function ForecastView({ month, year, userId, onMonthChange }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [monthData, setMonthData] = useState(null);
    const [recurrentTransactions, setRecurrentTransactions] = useState([]);

    const handleAuthError = (error) => {
        if (
            error.status === 401 ||
            error.code === 'SESSION_EXPIRED' ||
            error.code === 'INVALID_TOKEN' ||
            error.message?.includes("Token d'authentification requis")
        ) {
            navigate('/login');
            return true;
        }
        return false;
    };

    useEffect(() => {
        if (!userId) return;
        let cancelled = false;
        const load = async () => {
            setLoading(true);
            try {
                const monthKey = `${year}-${String(month + 1).padStart(2, '0')}`;
                const data = await transactionService.getMonth(monthKey);
                if (cancelled) return;
                setMonthData(data);

                const allTemplates = await recurringService.getActiveTemplates();
                if (cancelled) return;
                const valid = (allTemplates || []).filter((tpl) =>
                    isTemplateValidForMonth(tpl, month, year)
                );
                setRecurrentTransactions(valid);
            } catch (error) {
                console.error('Error loading forecast:', error);
                handleAuthError(error);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [month, year, userId]);

    const isTransactionAlreadyDone = (tpl) => {
        if (!monthData?.transactions) return false;
        const name = (tpl.title || tpl.description || '').toLowerCase().trim();
        if (!name) return false;
        return monthData.transactions.some(
            (tx) => (tx.description || '').toLowerCase().trim() === name
        );
    };

    const forecast = useMemo(
        () => computeForecast(recurrentTransactions, monthData, isTransactionAlreadyDone),
        [recurrentTransactions, monthData]
    );

    const grouped = useMemo(() => groupByDay(recurrentTransactions), [recurrentTransactions]);

    const today = new Date();
    const isCurrentMonth =
        today.getFullYear() === year && today.getMonth() === month;
    const isPastMonth =
        year < today.getFullYear() ||
        (year === today.getFullYear() && month < today.getMonth());

    const getTense = (day) => {
        if (isPastMonth) return 'past';
        if (!isCurrentMonth) return 'future';
        const d = today.getDate();
        if (day < d) return 'past';
        if (day === d) return 'today';
        return 'future';
    };

    const monthLabelLow = (MONTHS_FR[month] || '').toLowerCase();
    const forecastPositive = forecast.forecastBalance >= 0;

    if (loading && !monthData) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', padding: 40, color: t.textDim }}>
                <Spinner className="w-8 h-8" />
            </div>
        );
    }

    return (
        <div style={{ color: t.text, fontFamily: t.fontUi }}>
            <ForecastHero
                month={month}
                year={year}
                monthLabelLow={monthLabelLow}
                forecastBalance={forecast.forecastBalance}
                forecastPositive={forecastPositive}
                pendingCount={forecast.pending.length}
                onMonthChange={onMonthChange}
            />

            <ForecastStatsRow forecast={forecast} />

            <ForecastCalendarHeader
                totalCount={recurrentTransactions.length}
                doneCount={forecast.done.length}
            />

            <div>
                {grouped.length === 0 ? (
                    <ForecastEmptyState />
                ) : (
                    grouped.map(([day, txs]) => (
                        <ForecastSageDayGroup
                            key={day}
                            day={day}
                            month={month}
                            txs={txs}
                            isDone={isTransactionAlreadyDone}
                            tense={getTense(day)}
                        />
                    ))
                )}
            </div>

            {loading && monthData && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        backdropFilter: 'blur(4px)',
                        WebkitBackdropFilter: 'blur(4px)',
                        display: 'grid',
                        placeItems: 'center',
                        zIndex: 40,
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            background: t.surface,
                            border: `1px solid ${t.borderS}`,
                            borderRadius: 14,
                            padding: '12px 18px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            color: t.text,
                            fontFamily: t.fontUi,
                            fontSize: 13,
                        }}
                    >
                        <Spinner className="w-5 h-5" />
                        <span>Chargement…</span>
                    </div>
                </div>
            )}

            <style>{`
                @media (max-width: 860px) {
                    .forecast-stats-row { grid-template-columns: 1fr !important; }
                }
                @media (max-width: 640px) {
                    .forecast-sage-tx {
                        grid-template-columns: 36px 1fr auto !important;
                    }
                    .forecast-sage-tx > span:last-child { display: none !important; }
                }
                /* Paysage mobile : la rangée reste sur une ligne, comme dans la
                   vue Transactions. Doit suivre la règle max-width: 860px. */
                @media (orientation: landscape) and (max-height: 520px) {
                    .forecast-stats-row {
                        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
                        margin-bottom: 10px;
                    }
                }
            `}</style>
        </div>
    );
}
